import { useTranslation } from 'react-i18next';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { rankBadgeSrc } from '@/lib/ranks/rankBadgeSrc';
import { formatRankTierRequirement } from '@/lib/ranks/rankTierRequirement';

const SIZE_PX = {
  sm: 24,
  md: 40,
  lg: 64,
} as const;

const SIZE_CLASS = {
  sm: 'h-6 w-6',
  md: 'h-10 w-10',
  lg: 'h-16 w-16',
} as const;

type RankPatentBadgeSize = keyof typeof SIZE_CLASS;

type RankPatentBadgeProps = {
  tierId: string;
  size?: RankPatentBadgeSize;
  showLabel?: boolean;
  earned?: boolean;
  /**
   * Renders a hoverable/focusable tooltip button. Turn it off where the name
   * and requirement are already spelled out next to the badge (the ladder):
   * the tooltip would repeat them, and Radix's dialog autofocus would pop it
   * over the page the moment the dialog opens.
   */
  interactive?: boolean;
  seasonName?: string | null;
  className?: string;
  'data-testid'?: string;
};

export function RankPatentBadge({
  tierId,
  size = 'md',
  showLabel = true,
  earned = true,
  interactive = true,
  seasonName,
  className,
  'data-testid': testId,
}: RankPatentBadgeProps) {
  const { t, i18n } = useTranslation();
  const src = rankBadgeSrc(tierId);
  const label = t(`ranks.tiers.${tierId}`);
  const requirement = formatRankTierRequirement({
    tierId,
    language: i18n.language,
    t: (key, values) => t(key, values),
    seasonName,
  });
  const tooltip = requirement
    ? t('ranks.tierTooltip', { name: label, requirement })
    : label;
  const px = SIZE_PX[size];

  const picture = src ? (
    <img
      src={src}
      alt=""
      width={px}
      height={px}
      className={cn('h-full w-full rounded-full object-cover', !earned && 'grayscale')}
    />
  ) : null;

  const image = !src ? null : !interactive ? (
    <span className={cn('shrink-0', SIZE_CLASS[size])} aria-hidden="true">
      {picture}
    </span>
  ) : (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className={cn(
              'shrink-0 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              SIZE_CLASS[size],
            )}
            aria-label={tooltip}
          >
            {picture}
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs space-y-0.5">
          <p className="font-medium">{label}</p>
          {requirement ? (
            <p className="text-muted-foreground">{requirement}</p>
          ) : null}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  return (
    <span
      data-testid={testId}
      className={cn('inline-flex items-center gap-1.5 min-w-0', className)}
    >
      {image}
      {showLabel ? (
        <span className="min-w-0 truncate">{label}</span>
      ) : null}
    </span>
  );
}
